import { useEffect, useState } from "react";
import {
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FormStyleCollection as formStyles } from "../../styles/form";
import { AddCircle, Delete, ContentCopy } from "@mui/icons-material";
import { isEmpty } from "../../utils/Util";
import { useGetOnboardedDealersByMerchantQuery } from "../../services/API/surchargeApiSlice";
import { useGetMerchantsQuery } from "../../services/API/commonApiSlice";
import {
  useGetAllEventsQuery,
  usePerformWebHookHealthCheckMutation,
  useCreateWebHookSecretMutation,
  useCreateWebHookSubscriberMutation
} from "../../services/API/webHookApiSlice";
import {
  CreateWebHookSubscriberRequest,
  CreateWebHookSecretRequest,
  performHealthCheckRequest,
  WebHookEvent,
} from "../../types/WebHookTypes";
import { toast } from "react-toastify";
import LockIcon from "@mui/icons-material/Lock";
import EventSchema from "../WNF/ConfigureWebHook.EventSchema";
import { useNavigate, Navigate } from "react-router-dom";
import {
  validateUrl,
  copyText,
  handleApiError,
  useCommonKVP,
} from "./webhook.common";
import { useFlags } from "launchdarkly-react-client-sdk";

export default function ConfigureWebHook() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSecretProcessing, setIsSecretProcessing] = useState(false);
  const [isHealthCheckProcessing, setIsHealthCheckProcessing] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] =
    useState<CreateWebHookSubscriberRequest>(null);
  const [secretData, setSecretData] =
    useState<CreateWebHookSecretRequest>(null);

  const [dealersList, setDealersList] = useState<any>([]);
  const [selectedDealers, setSelectedDealers] = useState<any>([]);
  const [isDealerDisabled, setIsDealerDisabled] = useState(true);

  const [merchantList, setMerchantList] = useState<any>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);

  // Full event list from API
  const [eventList, setEventList] = useState<WebHookEvent[]>([]);
  // First-level dropdown: standalone events + group-name pseudo-options
  const [firstLevelOptions, setFirstLevelOptions] = useState<any[]>([]);
  // What the user picked in the first dropdown (standalone event object OR group pseudo-option)
  const [selectedFirstLevel, setSelectedFirstLevel] = useState<any>(null);
  // Sub-event options shown when a group is selected
  const [subEventOptions, setSubEventOptions] = useState<WebHookEvent[]>([]);
  // Multi-selected sub-events (only relevant when a group is selected)
  const [selectedSubEvents, setSelectedSubEvents] = useState<WebHookEvent[]>(
    [],
  );

  const [schemaEvent, setSchemaEvent] = useState<any>(null);

  const [isValidEndPoint, setIsValidEndPoint] = useState<any>(false);
  const [isValidDeadUrl, setIsValidDeadUrl] = useState<any>(false);
  const [emailError, setEmailError] = useState<any>(false);

  const [healthCheckId, setHealthCheckId] = useState<any>(null);
  const [healthCheckedURL, setHealthCheckedURL] = useState<any>(null);

  const [secretId, setSecretId] = useState<any>(null);
  const [secretCode, setSecretCode] = useState<any>("");

    const {
        rows,
        handleKVPChange,
        handleAddRow,
        handleRemoveRow,
    } = useCommonKVP();

    // Get Launch Darkly Flags
  const flags = useFlags();
  const allowWebhookIntegration = flags.allowWebhookIntegrations;

  const [performHealthCheck] = usePerformWebHookHealthCheckMutation();
  const [createSecret] = useCreateWebHookSecretMutation();
  const [submitForm] = useCreateWebHookSubscriberMutation();
  

  const { data: dealersByMerchantData, isFetching: isDealerFetching } = useGetOnboardedDealersByMerchantQuery(
    selectedMerchant?.MerchantId,
    { skip: !selectedMerchant?.MerchantId, refetchOnMountOrArgChange: true }
  );
  const { data: merchantData, isFetching: isMerchantFetching } =
    useGetMerchantsQuery({ refetchOnMountOrArgChange: true });

  const { data: eventData, isFetching: isEventFetching } = useGetAllEventsQuery(
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    document.title = "Configure Webhook";
    setFormData((prev) => ({...prev, ["apiVersion"]: "v1",  ["isActive"]: false, }));
    if (dealersByMerchantData) {
      setDealersList(dealersByMerchantData);
    }
    if (merchantData) {
      setMerchantList(merchantData);
    }
    if (eventData) {
      setEventList(eventData);
      // Standalone events (no group)
      const standalones = eventData.filter(
        (e: WebHookEvent) => !e.eventGroupName,
      );
      // Distinct group-name pseudo-options
      const groups = Array.from(
        new Set(
          eventData
            .filter((e: WebHookEvent) => e.eventGroupName)
            .map((e: WebHookEvent) => e.eventGroupName as string),
        ),
      ).map((name) => ({ isGroup: true, groupName: name, eventType: name }));
      setFirstLevelOptions([...standalones, ...groups]);
    }
  }, [
    dealersByMerchantData,
    isDealerFetching,
    merchantData,
    isMerchantFetching,
    eventData,
    isEventFetching,
  ]);

  const handleFirstLevelChange = (_: any, value: any) => {
    setSelectedFirstLevel(value);
    setSelectedSubEvents([]);
    if (value?.isGroup) {
      setSubEventOptions(
        eventList.filter((e) => e.eventGroupName === value.groupName),
      );
    } else {
      setSubEventOptions([]);
    }
  };

  const performHealthCheckPing = async () => {
    const payload: performHealthCheckRequest = {
      endpointUrl: formData?.endpointUrl,
      triggeredBy: "Manual",
    };

    try {
      setIsHealthCheckProcessing(true);
      const response = await performHealthCheck(payload).unwrap();
      if (response !== undefined && response.pingStatus === "Success") {
        toast.success(response?.message);
        setHealthCheckId(response?.healthCheckId);
        setHealthCheckedURL(payload.endpointUrl);
      } else if (response !== undefined && response.pingStatus === "Failed") {
        toast.error(response?.message);
      } else {
        toast.error("An unexpected error occurred.");
      }
    } catch (err) {
      handleApiError(err);
    }
    setIsHealthCheckProcessing(false);
  };

  const createWebHookSecret = async () => {
    const payload: CreateWebHookSecretRequest = {
      securityType: secretData?.securityType,
      userName: secretData?.userName ? secretData?.userName : null,
      password: secretData?.password ? secretData?.password : null,
    };

    try {
      setIsSecretProcessing(true);
      const response = await createSecret(payload).unwrap();
      if (response !== undefined && response.id) {
        setSecretId(response?.id);
        if (secretData?.securityType === "HMAC") {
          toast.success("HMAC generated Successfully!");
          setSecretCode(response?.secret);
        } else {
          toast.success("Credentials locked Successfully!");
        }
      } else {
        toast.error("An unexpected error occurred.");
      }
    } catch (err) {
      handleApiError(err);
    }
    setIsSecretProcessing(false);
  };

  const handleChange = (field: string) => (event: any) => {
    const value =
        field === "isActive" ? event.target.checked : event.target.value;

    if (field === "endpointUrl")
        setIsValidEndPoint(value !== "" && !validateUrl(value));

    if (field === "deadLetterUrl")
        setIsValidDeadUrl(value !== "" && !validateUrl(value));

    if (field === "endSystemOwnerEmail")
      setEmailError(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value));
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSecurityChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setSecretData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDropDownSelection = (value: any, type: string) => {
    if (type === "Merchants") {
      setSelectedMerchant(value);
      setSelectedDealers([]);
      setDealersList([]);
      setIsDealerDisabled(!value);
    }
    if (type === "Dealers") setSelectedDealers(value);
  };

  function validateForm(): boolean {
    if (selectedMerchant == null) {
      toast.error("Please select the merchant!");
      return false;
    }
    if (!formData?.description) {
      toast.error("Invalid input. Missing Description!");
      return false;
    }
    if (!formData?.endpointUrl || isValidEndPoint) {
      toast.error("Invalid input. Missing Endpoint URL!");
      return false;
    }
    if (!healthCheckId || formData?.endpointUrl !== healthCheckedURL) {
      toast.error("Action required. Please perform the health check!");
      return false;
    }
    if (!formData?.preferredHttpMethod) {
      toast.error("Invalid input. Missing Http Method!");
      return false;
    }
    if (!formData?.deliveryFormat) {
      toast.error("Invalid input. Missing delivery format!");
      return false;
    }
    if (!secretData?.securityType) {
      toast.error("Invalid input. Missing security type!");
      return false;
    }
    if (!secretId) {
      toast.error(
        "Action required. Please lock the secret or generate the code!",
      );
      return false;
    }
    if (!selectedFirstLevel) {
      toast.error("Please select an event or event group!");
      return false;
    }
    if (selectedFirstLevel.isGroup && selectedSubEvents.length === 0) {
      toast.error("Please select at least one sub-event!");
      return false;
    }
    if (!formData?.endSystemOwnerEmail || emailError) {
      toast.error("Invalid input. Missing email!");
      return false;
    }
    return true;
  }

  const handleSubmission = async () => {
    try {
      setIsProcessing(true);
      if (validateForm()) {
        const headerString = rows
          .filter((kvp) => kvp.key && kvp.value)
          .map((kvp) => `${kvp.key}:${kvp.value}`)
          .join(",");

        const eventTypeIds = selectedFirstLevel.isGroup
          ? selectedSubEvents.map((e) => e.id)
          : [selectedFirstLevel.id];

        const payload = {
          ...formData,
          ["merchantId"]: selectedMerchant?.MerchantId,
          ["DealerIds"]: selectedDealers?.map((item) => item.dealerId),
          ["healthCheckId"]: healthCheckId,
          ["secretId"]: secretId,
          ["EventTypeIds"]: eventTypeIds,
          ["customHeader"]: headerString,
        };

        const response = await submitForm(payload).unwrap();
        if (response !== undefined && response.id) {
          toast.success(`WebHook configured successfully!`);
          setTimeout(() => navigate("/dashboard/manageWebHooks"), 2000);
        } else {
          toast.error("Failed to process the WebHook.");
        }
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const isSaveDisabled =
    !selectedMerchant ||
    !formData?.description ||
    !formData?.endpointUrl ||
    isValidEndPoint ||
    !formData?.endSystemOwnerEmail ||
    emailError ||
    !secretData?.securityType ||
    !selectedFirstLevel ||
    (selectedFirstLevel?.isGroup && selectedSubEvents.length === 0);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ mt: 4 }}
    >
      {allowWebhookIntegration === false && <Navigate to="/401" />}
      <Card elevation={3} sx={{ width: 680, p: 4, borderRadius: 3 }}>
        <Typography
          variant="h6"
          sx={{ color: "primary.main", mb: 3, fontWeight: 600 }}
        >
          Configure WebHook
        </Typography>

        {/* General */}
        <Accordion defaultExpanded sx={{ mb: 3, borderRadius: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            style={{ backgroundColor: "#f9f9f9" }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              General
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel
              control={
                <Switch
                  value={formData?.isActive}
                  onChange={handleChange("isActive")}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center">
                  Enabled?
                  <Tooltip title="Enable or disable this webhook">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
            <Autocomplete
              id="merchants"
              autoHighlight
              options={merchantList}
              value={selectedMerchant}
              getOptionLabel={(option) => option.Name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Merchant *"
                  size="small"
                  variant="standard"
                  fullWidth
                />
              )}
              onChange={(event, value) =>
                handleDropDownSelection(value || null, "Merchants")
              }
              style={{ marginTop: "10px" }}
            />

            <Box style={formStyles.fieldSpacer}>
              <Autocomplete
                fullWidth
                multiple
                id="combo-box-demo"
                disabled={isDealerDisabled}
                disableCloseOnSelect
                options={dealersList}
                value={selectedDealers || []}
                getOptionLabel={(option: any) =>
                  `${option.dealerName} - ${option.dealerId}`
                }
                onChange={(event, value) =>
                  handleDropDownSelection(value || null, "Dealers")
                }
                renderOption={(props, option, { selected }) => {
                  const isDisabled = selectedDealers.some(
                    (element) =>
                      element.dealerId === option.dealerId &&
                      !isEmpty(element.requestedDate),
                  );
                  return (
                    <li
                      {...props}
                      style={{ pointerEvents: isDisabled ? "none" : "auto" }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            style={{ padding: "2px 5px" }}
                            checked={selected}
                          />
                        }
                        label={`${option.dealerName} - ${option.dealerId}`}
                      />
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label="Select Dealers"
                    InputProps={{ ...params.InputProps, endAdornment: "" }}
                    inputProps={{ ...params.inputProps }}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.dealerId === value.dealerId
                }
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => {
                    const isDisabled = selectedDealers.some(
                      (element) =>
                        element.dealerId === option.dealerId &&
                        !isEmpty(element.requestedDate),
                    );
                    return (
                      <Chip
                        key={option.dealerId}
                        label={`${option.dealerName} - ${option.dealerId}`}
                        {...getTagProps({ index })}
                        disabled={isDisabled}
                        style={{
                          pointerEvents: isDisabled ? "none" : "auto",
                          color: isDisabled ? "gray" : "inherit",
                        }}
                      />
                    );
                  })
                }
              />
            </Box>

            <TextField
              sx={{ mb: 3 }}
              label="Api Version"
              fullWidth
              disabled
              value={formData?.apiVersion}
              margin="normal"
              variant="standard"
            />
            <TextField
              sx={{ mb: 3 }}
              label="Description *"
              fullWidth
              multiline
              margin="normal"
              variant="standard"
              value={formData?.description}
              onChange={handleChange("description")}
            />
          </AccordionDetails>
        </Accordion>

        {/* Server Config */}
        <Accordion sx={{ mb: 3, borderRadius: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            style={{ backgroundColor: "#f9f9f9" }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Server Configurations
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              variant="standard"
              label="Endpoint URL *"
              value={formData?.endpointUrl}
              onChange={handleChange("endpointUrl")}
              error={isValidEndPoint}
              helperText={isValidEndPoint ? "Enter a valid URL" : ""}
              fullWidth
              margin="normal"
              type="url"
            />
            <Box>
              <Button
                style={{ float: "right" }}
                variant="outlined"
                onClick={performHealthCheckPing}
                disabled={
                  isValidEndPoint ||
                  !formData?.endpointUrl ||
                  formData?.endpointUrl == healthCheckedURL
                }
              >
                {isHealthCheckProcessing ? (
                  <CircularProgress size={25} color="info" />
                ) : (
                  "Health Test"
                )}
              </Button>
            </Box>
            <FormControl variant="standard" fullWidth margin="normal">
              <InputLabel>Http Method *</InputLabel>
              <Select
                value={formData?.preferredHttpMethod}
                onChange={handleChange("preferredHttpMethod")}
              >
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="standard" fullWidth margin="normal">
              <InputLabel>Delivery Format *</InputLabel>
              <Select
                value={formData?.deliveryFormat}
                onChange={handleChange("deliveryFormat")}
              >
                <MenuItem value="json">JSON</MenuItem>
              </Select>
            </FormControl>
          </AccordionDetails>
        </Accordion>

        {/* Security */}
        <Accordion sx={{ mb: 3, borderRadius: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            style={{ backgroundColor: "#f9f9f9" }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Security
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControl variant="standard" fullWidth margin="normal">
              <InputLabel>Security Type *</InputLabel>
              <Select
                value={secretData?.securityType}
                onChange={handleSecurityChange("securityType")}
                disabled={secretId}
              >
                <MenuItem value="Basic">Basic</MenuItem>
                <MenuItem value="HMAC">HMAC</MenuItem>
              </Select>
            </FormControl>
            {secretData?.securityType == "Basic" && (
              <>
                <TextField
                  value={secretData?.userName}
                  variant="standard"
                  label="Username"
                  fullWidth
                  margin="normal"
                  onChange={handleSecurityChange("userName")}
                  disabled={secretId}
                  InputProps={{
                    endAdornment: secretId ? (
                      <InputAdornment position="end">
                        <LockIcon color="disabled" />
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <TextField
                  value={secretData?.password}
                  variant="standard"
                  label="Password"
                  type="password"
                  fullWidth
                  margin="normal"
                  onChange={handleSecurityChange("password")}
                  disabled={secretId}
                  InputProps={{
                    endAdornment: secretId ? (
                      <InputAdornment position="end">
                        <LockIcon color="disabled" />
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Box>
                  <Button
                    style={{ float: "right", marginBottom: 20, marginTop: 10 }}
                    variant="outlined"
                    onClick={createWebHookSecret}
                    disabled={
                      !secretData?.userName || !secretData?.password || secretId
                    }
                  >
                    {isSecretProcessing ? (
                      <CircularProgress size={25} color="info" />
                    ) : (
                      "Lock Credentials"
                    )}
                  </Button>
                </Box>
              </>
            )}
            {secretData?.securityType == "HMAC" && (
              <>
                <TextField
                  value={secretCode || ""}
                  variant="standard"
                  label="HMAC Code"
                  fullWidth
                  margin="normal"
                  disabled
                  InputProps={{
                    endAdornment: secretCode ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => copyText(secretCode)}
                          edge="end"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Box>
                  <Button
                    style={{ float: "right", marginBottom: 20, marginTop: 10 }}
                    variant="outlined"
                    onClick={createWebHookSecret}
                    disabled={secretId}
                  >
                    {isSecretProcessing ? (
                      <CircularProgress size={25} color="info" />
                    ) : (
                      "Generate"
                    )}
                  </Button>
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Events */}
        <Accordion sx={{ mb: 3, borderRadius: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            style={{ backgroundColor: "#f9f9f9" }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Events
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Level 1: standalone events + group headings */}
            <Autocomplete
              options={firstLevelOptions}
              value={selectedFirstLevel}
              getOptionLabel={(option: any) =>
                option.eventType || option.groupName
              }
              isOptionEqualToValue={(option: any, value: any) =>
                option.isGroup
                  ? option.groupName === value.groupName
                  : option.id === value.id
              }
              onChange={handleFirstLevelChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Select Event *"
                  size="small"
                  fullWidth
                />
              )}
            />

            {/* Level 2: sub-events (only when a group is selected) */}
            {selectedFirstLevel?.isGroup && (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={subEventOptions}
                value={selectedSubEvents}
                getOptionLabel={(option: any) => option.eventType}
                isOptionEqualToValue={(option: any, value: any) =>
                  option.id === value.id
                }
                onChange={(_, value) => setSelectedSubEvents(value || [])}
                renderTags={(selected, getTagProps) =>
                  selected.map((option: any, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {option.eventType}
                          <InfoOutlinedIcon
                            fontSize="inherit"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSchemaEvent(option);
                            }}
                          />
                        </Box>
                      }
                    />
                  ))
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={{ padding: "2px 5px" }}
                          checked={selected}
                        />
                      }
                      label={(option as any).eventType}
                    />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label="Select Sub-Events *"
                    size="small"
                    fullWidth
                    helperText="Select at least one sub-event"
                    sx={{ mt: 2 }}
                  />
                )}
              />
            )}

            {/* Event schema details */}
            {selectedFirstLevel && !selectedFirstLevel.isGroup && (
              <Box>
                <button
                  onClick={() => setSchemaEvent(selectedFirstLevel)}
                  style={{
                    float: "right",
                    marginTop: "10px",
                    borderRadius: 6,
                    marginBottom: 10,
                  }}
                >
                  Event details
                </button>
              </Box>
            )}

            {schemaEvent && (
              <EventSchema
                open={!!schemaEvent}
                onClose={() => setSchemaEvent(null)}
                schemaDetails={schemaEvent}
              />
            )}
          </AccordionDetails>
        </Accordion>

        {/* Additional Settings */}
        <Accordion sx={{ mb: 3, borderRadius: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            style={{ backgroundColor: "#f9f9f9" }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Additional Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControl variant="standard" fullWidth margin="normal">
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Add Custom Headers (KVP)
              </Typography>
              {rows.map((row, index) => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  key={index}
                  sx={{ mb: 1 }}
                >
                  <Grid item xs={5}>
                    <TextField
                      label="Key"
                      variant="outlined"
                      fullWidth
                      value={row.key}
                      onChange={(e) =>
                        handleKVPChange(index, "key", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Value"
                      variant="outlined"
                      fullWidth
                      value={row.value}
                      onChange={(e) =>
                        handleKVPChange(index, "value", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <Box display="flex" justifyContent="center">
                      {index === rows.length - 1 ? (
                        <IconButton color="primary" onClick={handleAddRow}>
                          <AddCircle />
                        </IconButton>
                      ) : (
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveRow(index)}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              ))}
            </FormControl>
            <TextField
              value={formData?.endSystemOwnerEmail}
              variant="standard"
              label="System Owner Email *"
              type="email"
              fullWidth
              margin="normal"
              onChange={handleChange("endSystemOwnerEmail")}
              error={emailError}
              helperText={
                emailError ? "Please enter a valid email address" : ""
              }
            />
          </AccordionDetails>
        </Accordion>

        {/* Save Button */}
        <Box style={{ padding: "10px 10px 10px 0px", textAlign: "left" }}>
          <Button
            style={formStyles.fieldSpacer}
            variant="contained"
            color="primary"
            onClick={handleSubmission}
            disabled={isSaveDisabled}
          >
            SAVE
          </Button>
          {(isProcessing || isMerchantFetching || isEventFetching) && (
            <CircularProgress
              size={25}
              style={{ marginLeft: "20px" }}
              color="info"
            />
          )}
        </Box>
      </Card>
    </Box>
  );
}
